import { Environment } from '../../../environment';
import { v4 as uuidv4 } from 'uuid';
import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';

export type TTipoBem = 'VEICULO' | 'MOTO' | 'MAQUINA' | 'EQUIPAMENTO' | 'PATRIMONIO' | 'OUTRO';

export interface IBem {
  idbem: string;
  // Opcional: bem pode existir sem cliente vinculado ainda, ver DetalheDeBem.tsx.
  idparceiro?: string;
  tipo: TTipoBem;
  descricao: string;
  marca?: string;
  modelo?: string;
  ano?: string;
  cor?: string;
  placa?: string;
  chassi?: string;
  renavam?: string;
  quilometragem?: number;
  combustivel?: string;
  horimetro?: number;
  numeroserie?: string;
  patrimonio?: string;
  observacao?: string;
  ativo: boolean;
}

type TotalCount = {
  data: IBem[];
  totalCount: number;
};

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const tratarErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) {
      return new Error(axiosError.response.data.errors?.default || 'Erro desconhecido');
    } else if (axiosError.request) {
      return new Error('Nenhuma resposta do servidor');
    } else {
      return new Error(axiosError.message);
    }
  }
  return new Error('Erro inesperado');
};

const getAll = async (page = 1, filter = '', idparceiro = ''): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = `/bens?page=${page}&limit=${Environment.LIMITE_DE_LINHAS}&filter=${filter}&idparceiro=${idparceiro}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_LINHAS),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    return tratarErro(error);
  }
};

// idparceiro/filter/id: mesmo truque de VeiculosService/ParceirosService —
// busca por id (sem digitar nada) quando o autocomplete já tem um valor
// selecionado, senão busca pelo texto digitado dentro dos bens do parceiro.
const getAllList = async (idparceiro = '', filter = '', id = ''): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = (filter === '' && id !== '')
      ? `/benslist/${id}`
      : `/benslist?limit=${Environment.LIMITE_DE_LINHAS}&idparceiro=${idparceiro}&filter=${filter}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_LINHAS),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    return tratarErro(error);
  }
};

const getById = async (id: string): Promise<IBem | Error> => {
  try {
    const { data } = await Api.get(`/bens/${id}`);
    if (data) return data;
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    return tratarErro(error);
  }
};

const create = async (dados: Omit<IBem, 'idbem'>): Promise<string | Error> => {
  try {
    const guuid = uuidv4();
    const { data } = await Api.post('/bens', { idbem: guuid, ...dados });
    if (Object.keys(data).length === 0) return guuid;
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    return tratarErro(error);
  }
};

const updateById = async (id: string, dados: Omit<IBem, 'idbem'>): Promise<void | Error> => {
  try {
    await Api.put(`/bens/${id}`, dados);
  } catch (error) {
    return tratarErro(error);
  }
};

export interface IOrdemDoBemHistorico {
  idos: string;
  numero: string;
  emissao: string;
  status: string;
  total: number;
}

export interface IOcorrenciaPecaHistorico {
  idos: string;
  numero: string;
  emissao: string;
  quantidade: number;
}

// Uma peça que já foi trocada mais de uma vez nesse bem — ocorrências em
// ordem cronológica, diasEntreTrocas[i] é o intervalo entre ocorrências[i]
// e ocorrências[i+1] (ver GetHistorico.ts, backend).
export interface IPecaRepetidaHistorico {
  chave: string;
  descricao: string;
  ocorrencias: IOcorrenciaPecaHistorico[];
  diasEntreTrocas: number[];
}

export interface IHistoricoBem {
  ordens: IOrdemDoBemHistorico[];
  pecasRepetidas: IPecaRepetidaHistorico[];
}

const getHistorico = async (idbem: string): Promise<IHistoricoBem | Error> => {
  try {
    const { data } = await Api.get(`/bens/${idbem}/historico`);
    if (data) return data;
    return new Error('Erro ao consultar o histórico do bem.');
  } catch (error) {
    return tratarErro(error);
  }
};

// Uma troca de dono do bem — nomeparceiro só vem preenchido quando
// idparceiro existe (linha pode representar "ficou sem dono", ver backend
// GetHistoricoParceiro.ts).
export interface IBemParceiroHistorico {
  idhistorico: string;
  idbem: string;
  idparceiro?: string;
  nomeparceiro?: string;
  criadoem: string;
}

const getHistoricoParceiro = async (idbem: string): Promise<IBemParceiroHistorico[] | Error> => {
  try {
    const { data } = await Api.get(`/bens/${idbem}/proprietarios`);
    if (data) return data;
    return new Error('Erro ao consultar o histórico de proprietários do bem.');
  } catch (error) {
    return tratarErro(error);
  }
};

export const BensService = {
  getAll,
  getAllList,
  getById,
  create,
  updateById,
  getHistorico,
  getHistoricoParceiro,
};
